import { ChevronsUpDown, Plus, Store, CheckCircle } from "lucide-react";
import { useState } from "react";
import { router, usePage } from "@inertiajs/react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";

export function TeamSwitcher() {
  const { isMobile } = useSidebar();
  const { props } = usePage();

  // Get store data from Inertia props
  const partnerStores = props.partner_stores || [];
  const currentStoreId = props.current_store_id;
  const currentStore = props.current_store || partnerStores.find((store) => store.id === currentStoreId);

  const [isLoading, setIsLoading] = useState(false);

  const handleStoreSwitch = (storeId) => {
    if (storeId === currentStoreId || isLoading) return;

    setIsLoading(true);

    router.post(
      "/partner/set_current_store",
      {
        store_id: storeId,
      },
      {
        onFinish: () => setIsLoading(false),
        onError: (errors) => {
          console.error("Store switch failed:", errors);
          setIsLoading(false);
        },
      },
    );
  };

  // If no stores available
  if (!partnerStores.length) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Store className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">No Store Found</span>
              <span className="truncate text-xs">Please create a store</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              disabled={isLoading}
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <Store className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{currentStore?.name || "Select Store"}</span>
                <span className="truncate text-xs">{currentStore?.status || "No status"}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">Your Stores</DropdownMenuLabel>
            {partnerStores.map((store, index) => (
              <DropdownMenuItem key={store.id} onClick={() => handleStoreSwitch(store.id)} className="gap-2 p-2" disabled={isLoading}>
                <div className="flex size-6 items-center justify-center rounded-sm border">
                  {store.id === currentStoreId ? (
                    <CheckCircle className="size-4 shrink-0 text-green-600" />
                  ) : (
                    <Store className="size-4 shrink-0" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{store.name}</div>
                  <div className="text-xs text-muted-foreground">{store.status}</div>
                </div>
                {index < 9 && <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 p-2" onClick={() => router.visit("/partner/settings/stores")}>
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">Manage Stores</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
